/**
 * The screening boards.
 *
 * The board is one query, not one per column: the four columns are a single answer to
 * a single question, and fetching them apart would let them disagree with each other
 * for as long as the slowest one took.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api/client';
import type {
  CandidateRead,
  FlystBoard,
  FlystStage,
  RsrBoard,
  RsrState,
  ScreeningKind,
  ScreeningNote,
} from '@/api/schema';
import { ATS_KEY } from '@/features/ats/api';
import { CANDIDATES_KEY } from '@/features/candidates/api';

export const SCREENINGS_KEY = ['screenings'] as const;

export type BoardFilters = {
  search?: string;
  department_id?: string;
  preferred_country_id?: string;
};

export function useFlystBoard(filters: BoardFilters) {
  return useQuery({
    queryKey: [...SCREENINGS_KEY, 'flyst', filters],
    queryFn: () => api.get<FlystBoard>('/api/v1/screenings/flyst', { query: filters }),
    // Kept while refetching, so moving a card does not blank the board underneath the
    // person who moved it.
    placeholderData: (previous) => previous,
  });
}

/** Move a card, and put it back if the server says no. */
export function useAdvanceFlyst(filters: BoardFilters) {
  const queryClient = useQueryClient();
  const key = [...SCREENINGS_KEY, 'flyst', filters];

  return useMutation({
    mutationFn: ({
      id,
      stage,
      handOff,
    }: {
      id: string;
      stage: FlystStage;
      /** The screening date; Calendar is managed separately. */
      handOff?: Record<string, string>;
    }) =>
      api.post<CandidateRead>(`/api/v1/candidates/${id}/screening/flyst/advance`, {
        stage,
        ...handOff,
      }),

    /**
     * The card moves before the server has agreed.
     *
     * A board where a card sits still for a round trip feels broken, so it moves at
     * once and the previous board is kept to put back. Any in-flight refetch is
     * cancelled first; otherwise it can land after this and overwrite the move with
     * the state from before it.
     */
    onMutate: async ({ id, stage }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<FlystBoard>(key);
      if (previous) queryClient.setQueryData<FlystBoard>(key, moveCard(previous, id, stage));
      return { previous };
    },

    onError: (_error, _variables, context) => {
      // Visibly back where it came from. The error itself is shown by the caller,
      // which knows which card it was.
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: SCREENINGS_KEY });
      // The register shows the stage too, and a candidate detail page may be open.
      await queryClient.invalidateQueries({ queryKey: CANDIDATES_KEY });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ATS_KEY });
    },
  });
}

/**
 * The same board with one card in a different column.
 *
 * Totals move with it, so the column headings stay honest during the moment before the
 * server answers. The card's waiting days are reset to zero because that is what the
 * server will say: it has just this second entered the new stage.
 */
export function moveCard(board: FlystBoard, id: string, stage: FlystStage): FlystBoard {
  const card = board.columns.flatMap((column) => column.cards).find((item) => item.id === id);
  if (!card) return board;

  return {
    ...board,
    columns: board.columns.map((column) => {
      if (column.stage === card.flyst_stage) {
        return {
          ...column,
          total: column.total - 1,
          cards: column.cards.filter((item) => item.id !== id),
        };
      }
      if (column.stage === stage) {
        return {
          ...column,
          total: column.total + 1,
          cards: [{ ...card, flyst_stage: stage, waiting_days: 0 }, ...column.cards],
        };
      }
      return column;
    }),
  };
}

// --- RSR ------------------------------------------------------------------- //

export function useRsrBoard(filters: BoardFilters) {
  return useQuery({
    queryKey: [...SCREENINGS_KEY, 'rsr', filters],
    queryFn: () => api.get<RsrBoard>('/api/v1/screenings/rsr', { query: filters }),
    placeholderData: (previous) => previous,
  });
}

/**
 * Which columns a card can be dropped into.
 *
 * Not every column is a decision. Arranged means "there is a date", so it is reached by
 * giving one rather than by dragging; Happened means the date has gone by, which is a
 * fact about the calendar and not something to declare. Refusing those two drops in the
 * browser matches what the server would say and saves the reader a 409 that only
 * repeats the rule.
 */
export function canDropOnRsr(state: RsrState): boolean {
  return state !== 'happened';
}

/** Move a card on the RSR board, and put it back if the server says no. */
export function useAdvanceRsr(filters: BoardFilters) {
  const queryClient = useQueryClient();
  const key = [...SCREENINGS_KEY, 'rsr', filters];

  return useMutation({
    mutationFn: ({
      id,
      state,
      scheduledOn,
      note,
    }: {
      id: string;
      state: RsrState;
      scheduledOn?: string | null;
      note?: string;
    }) =>
      api.post<CandidateRead>(`/api/v1/candidates/${id}/screening/rsr/advance`, {
        state,
        rsr_scheduled_on: scheduledOn ?? null,
        note,
      }),

    onMutate: async ({ id, state, scheduledOn }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<RsrBoard>(key);
      if (previous) {
        queryClient.setQueryData<RsrBoard>(key, moveRsrCard(previous, id, state, scheduledOn));
      }
      return { previous };
    },

    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: SCREENINGS_KEY });
      await queryClient.invalidateQueries({ queryKey: CANDIDATES_KEY });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ATS_KEY });
    },
  });
}

/**
 * The same board with one card in a different column.
 *
 * The booking moves with the state, because on this board the two are the same fact:
 * arranging sets a date and going back to pending takes it away. Showing the old date
 * under the new column for the length of a round trip would contradict the column the
 * card had just landed in.
 */
export function moveRsrCard(
  board: RsrBoard,
  id: string,
  state: RsrState,
  scheduledOn?: string | null,
): RsrBoard {
  const card = board.columns.flatMap((column) => column.cards).find((item) => item.id === id);
  if (!card) return board;

  const booked = state === 'pending' ? null : (scheduledOn ?? card.rsr_scheduled_on);

  return {
    ...board,
    columns: board.columns.map((column) => {
      if (column.state === card.rsr_state) {
        return {
          ...column,
          total: column.total - 1,
          cards: column.cards.filter((item) => item.id !== id),
        };
      }
      if (column.state === state) {
        return {
          ...column,
          total: column.total + 1,
          cards: [{ ...card, rsr_state: state, rsr_scheduled_on: booked }, ...column.cards],
        };
      }
      return column;
    }),
  };
}

// --- notes ----------------------------------------------------------------- //

export function useScreeningNotes(candidateId: string | null, kind: ScreeningKind) {
  return useQuery({
    queryKey: [...SCREENINGS_KEY, 'notes', candidateId, kind],
    queryFn: () =>
      api.get<ScreeningNote[]>(
        `/api/v1/candidates/${candidateId ?? ''}/screening/${kind}/notes`,
      ),
    enabled: candidateId !== null,
  });
}

/**
 * Write a note.
 *
 * Only the words are sent. Which screening it belongs to is in the URL, the stage is
 * read off the candidate by the server, and the author is the session. None of the
 * three is the client's to assert.
 */
export function useAddScreeningNote(candidateId: string, kind: ScreeningKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      api.post<ScreeningNote>(`/api/v1/candidates/${candidateId}/screening/${kind}/notes`, {
        body,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [...SCREENINGS_KEY, 'notes'] });
      // The badge on the board card is a count from the board query.
      await queryClient.invalidateQueries({ queryKey: SCREENINGS_KEY });
    },
  });
}
